import { normaliseIntensityFields } from "@bcc/components/shopify-import/product-form/product-form.utils";
import type { ProductFormData } from "@bcc/components/shopify-import/schemas/product-schema";
import {
  DEFAULT_TAX_RATES_BY_REGION,
  parseBagSizeToKg,
  parseShipToCountries,
  resolveDeDomesticWholesalePrice,
  type TaxRatesByRegion,
} from "@bcc/lib/products/cross-border-tax";
import { parsePrice } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import { blobToBase64, buildAgtronEstimateFields, getLocalisedFieldByDefaultOrEnglish } from "@client-shared/utilities/productSelectModal";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import type { SalesforceProduct } from "@shared/services";
import type { DropZoneFile, FormProduct, FormVariant, LocaleObject, LocalizedFields, ProductImage, UploadImage } from "@shared/types";
import { isIntensityPosition, toSalesforceIntensityZone } from "@shared/types/intensity";

export const setSsSkuOnboardingModalTestId = (identifier: string): string => generateTestId("SsSkuOnboardingModal", identifier);

/** Converts catalog `ProductImage[]` (PIM `src` URLs) into the `UploadImage[]` shape
 *  the ProductForm renders from. Used in "new bag size for existing SKU" mode so the
 *  parent SKU's image surfaces in the read-only image slot. Returns undefined when
 *  there are no images to hydrate so the form falls back to its empty state. */
export const setImageUploadsFromProductImages = (images: readonly ProductImage[] | undefined): UploadImage[] | undefined => {
  if (!images?.length) return undefined;
  return images.map((image) => ({
    type: "url" as const,
    url: image.src,
    width: image.width,
    height: image.height,
  }));
};

/** The image schema types `file` as `unknown`; at runtime the FileUpload
 *  component always stores a `DropZoneFile` (a spread File + `preview`), so
 *  narrow on its one required own-property. */
const isDropZoneFile = (file: unknown): file is DropZoneFile => typeof file === "object" && file !== null && "preview" in file;

/** Re-types the form's image selection as `UploadImage[]`, preserving the
 *  null-vs-undefined distinction (`null` = deliberately empty selection) and
 *  dropping any file entry whose payload doesn't narrow to a `DropZoneFile`. */
const uploadImagesFromFormData = (images: ProductFormData["imageUploads"]): UploadImage[] | null | undefined => {
  if (images == null) return images;
  return images.flatMap((image): UploadImage[] => {
    if (image.type === "url") return [image];
    return isDropZoneFile(image.file) ? [{ ...image, file: image.file }] : [];
  });
};

/** Rebuilds a `FormVariant` from a form-values variant: restores the
 *  `type: "variant"` discriminator and collapses zod's `nullish` fields to the
 *  optional-only shapes `FormVariant` expects. */
const formVariantFromFormData = (variant: ProductFormData["variants"][number]): FormVariant => ({
  ...variant,
  type: "variant",
  bagSize: variant.bagSize ?? "",
  endDate: variant.endDate ?? undefined,
  weeklyInventory: variant.weeklyInventory ?? undefined,
  totalMaxShortTermInventory: variant.totalMaxShortTermInventory ?? undefined,
});

/** Inverse of `mapProductToFormDefaults`, used when saving a draft: rebuilds a
 *  properly typed `FormProduct` from the current (possibly incomplete — that's
 *  the point of a draft) form contents, layered over the product the session
 *  started from so non-form fields (type, serviceId, images, …) survive the
 *  round trip. The variant arrays are defensively optional at runtime:
 *  react-hook-form's `getValues()` reflects the registered defaults, not the
 *  static form type. */
export function mapFormDataToDraftProduct(initialProduct: FormProduct, formData: Partial<ProductFormData>, id: string): FormProduct {
  return {
    ...initialProduct,
    ...formData,
    id,
    variants: formData.variants?.map(formVariantFromFormData) ?? initialProduct.variants,
    localVariants: formData.localVariants?.map(formVariantFromFormData) ?? initialProduct.localVariants,
    imageUploads: formData.imageUploads === undefined ? initialProduct.imageUploads : uploadImagesFromFormData(formData.imageUploads),
    // Same nullish collapse as the variant fields above, shared with the
    // Shopify import form so both paths agree on what "unscored" looks like.
    // `intensityZone` resolves to `""` rather than `undefined` when unset —
    // deliberately looser than `FormProduct`'s own type — so a saved draft
    // can tell "cleared" apart from "never touched" after a JSON round trip.
    ...normaliseIntensityFields(formData),
  } as FormProduct;
}

export const EMPTY_SS_PRODUCT: FormProduct = {
  type: "product",
  id: "",
  productType: "",
  isProductFormValid: false,
  variants: [
    {
      type: "variant",
      id: "ss-variant-1",
      bagSize: "",
      grind: [],
      price: "",
      wholesalePrice: "",
      term: "Long",
      startDate: "",
      isProductSelectedForImport: true,
    },
  ],
};

/** Processes imageUploads from the ProductForm into a base64 or URL string for Salesforce.
 *  The FileUpload component spreads the File into a plain object ({ ...file, preview }),
 *  so we use the preview blob URL to fetch the actual Blob for base64 conversion. */
const processImageUpload = async (imageUploads: UploadImage[] | null | undefined): Promise<string | null> => {
  if (!imageUploads || imageUploads.length === 0) return null;
  const imageUpload = imageUploads[0];
  if (!imageUpload) return null;
  if (imageUpload.type === "url") {
    return imageUpload.url ?? null;
  }
  if (imageUpload.type === "file") {
    const file = imageUpload.file;
    // If file is a real Blob/File, convert directly
    if (file instanceof Blob) {
      const base64 = await blobToBase64(file);
      return base64.split("base64,")[1] as string;
    }
    // FileUpload spreads File into { ...file, preview } — use preview URL to get blob
    const preview = (file as { preview?: string })?.preview;
    if (preview) {
      const response = await fetch(preview);
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      return base64.split("base64,")[1] as string;
    }
  }
  return null;
};

/** The copyrighted / localised copy-editing fields sent to Salesforce. */
type CopyEditFields = Pick<
  SalesforceProduct,
  | "Name_of_coffee_copyrighted__c"
  | "Product_description_Copyrighted__c"
  | "Roaster_Comments_Copyrighted__c"
  | "Name_of_Coffee_Locale__c"
  | "Product_description_Locale__c"
  | "Roaster_Notes_Locale__c"
>;

const EMPTY_COPY_EDIT_FIELDS: CopyEditFields = {
  Name_of_coffee_copyrighted__c: null,
  Product_description_Copyrighted__c: null,
  Roaster_Comments_Copyrighted__c: null,
  Name_of_Coffee_Locale__c: null,
  Product_description_Locale__c: null,
  Roaster_Notes_Locale__c: null,
};

/** Builds the copy-editing fields for the Salesforce payload.
 *
 *  For a brand-new SKU these stay null — the Beanz Manager authors the copy
 *  during approval. When adding a NEW BAG SIZE to an EXISTING SKU
 *  the copy doesn't change per bag size, so we carry it over from the parent
 *  SKU: the English locale feeds the `*_Copyrighted__c` fields and the default
 *  locale feeds the `*_Locale__c` fields, mirroring the canonical new-SKU
 *  import mapping. We fall back to the (read-only, parent-prefilled) form
 *  values so the fields are never blank for an existing SKU even when the
 *  parent's per-locale copy is unavailable. */
function buildCopyEditFields(
  formData: ProductFormData,
  isNewBagSizeMode: boolean,
  localizedFields: LocalizedFields | undefined,
  regionLocales: LocaleObject[]
): CopyEditFields {
  if (!isNewBagSizeMode) {
    return EMPTY_COPY_EDIT_FIELDS;
  }

  const localised = getLocalisedFieldByDefaultOrEnglish(regionLocales, localizedFields);
  const coffeeName = formData.title || null;
  const description = formData.description || null;
  const roasterNotes = formData.roasterNotes || null;

  return {
    Name_of_coffee_copyrighted__c: coffeeName,
    Product_description_Copyrighted__c: localised("English", "description") ?? description,
    Roaster_Comments_Copyrighted__c: localised("English", "roasterNotes") ?? roasterNotes,
    Name_of_Coffee_Locale__c: coffeeName,
    Product_description_Locale__c: localised("default", "description") ?? description,
    Roaster_Notes_Locale__c: localised("default", "roasterNotes") ?? roasterNotes,
  };
}

/** Resolves the wholesale price to send to Salesforce for one variant. For a
 *  DE-domestic vendor this must be the Kaffeesteuer-excluded value (netto +
 *  VAT), matching the existing cross-border rule (BRG lodges the coffee tax
 *  on the roaster's behalf); every other vendor keeps sending the raw entered
 *  value unchanged. */
const resolveWholesalePriceForSalesforce = (
  variant: ProductFormData["variants"][number],
  isDeDomestic: boolean,
  ratesByRegion: TaxRatesByRegion
): number | string => {
  if (!isDeDomestic) return variant.wholesalePrice ?? 0;
  return resolveDeDomesticWholesalePrice(
    parsePrice(variant.price),
    parsePrice(variant.wholesalePrice),
    parseBagSizeToKg(variant.bagSize),
    ratesByRegion
  );
};

/**
 * The Salesforce intensity fields, normalised from the form's
 * "unscored" representation. Shared shape with the Shopify import mapper in
 * `salesforceProductTransformations.ts` so both submit paths agree.
 *
 * The position is always derived from the "most similar to" dropdown (the
 * scale is read-only), and that pick is sent as `Closest_Match_Coffee__c`.
 */
function buildIntensityPayload(
  formData: ProductFormData,
  isIntensityEstablished: boolean
): Partial<
  Pick<
    SalesforceProduct,
    "Intensity_Zone__c" | "Intensity_Position__c" | "Agtron_Score__c" | "Is_Agtron_Score_Provided__c" | "Closest_Match_Coffee__c"
  >
> {
  // A later bag size / grind of an established coffee must not write a new
  // score: omit the intensity keys so the established ones stay untouched. Only
  // the established "most similar to" pick is sent — explicitly null when this
  // record no longer has one, so an in-place resubmit cannot keep a stale pick.
  // (An unscored parent has nothing to send, and its read-only form could not
  // have supplied a score anyway, so bag-size mode implies "established".)
  if (isIntensityEstablished) {
    return { Closest_Match_Coffee__c: formData.closestMatchCoffee || null };
  }
  const { intensityPosition, intensityZone } = normaliseIntensityFields(formData);
  // Unscored coffee: send no intensity fields at all rather than nulls/blanks.
  if (!isIntensityPosition(intensityPosition)) return {};

  return {
    Intensity_Zone__c: toSalesforceIntensityZone(intensityZone),
    Intensity_Position__c: intensityPosition,
    ...buildAgtronEstimateFields(intensityPosition),
    Closest_Match_Coffee__c: formData.closestMatchCoffee || null,
  };
}

function buildVariantPayload(
  formData: ProductFormData,
  userId: string,
  vendorRegion: string,
  currencyIso: string,
  isNewBagSizeMode: boolean,
  imageDoc: string | null,
  copyEditFields: CopyEditFields,
  homeRegion: CrossBorderCountries | undefined,
  ratesByRegion: TaxRatesByRegion,
  isIntensityEstablished: boolean
): (variant: ProductFormData["variants"][number]) => SalesforceProduct {
  return (variant) => {
    // DE-domestic: a DE vendor whose bag size ships to at most its own country
    // — same detection the price-update/SKU-import UI uses. Ship-to is bag-size
    // based, so it is derived from the variant, not a product-level field.
    const isDeDomestic = homeRegion === "DE" && parseShipToCountries(variant.ship_to).length <= 1;

    return {
      // Product-level fields
      Name_of_coffee__c: formData.title,
      External_Product_ID__c: `SS-${globalThis.crypto.randomUUID()}`,
      Product_description__c: formData.description,
      Main_flavor_category__c: formData.mainFlavorCategory,
      Secondary_flavor_category__c: formData.secondaryFlavorCategory || null,
      Tasting_notes__c: formData.tastingNotes.join(";"),
      Is_this_coffee_decaf__c: formData.isDecaf,
      Type_of_coffee__c: formData.productType,
      Roast_level__c: formData.roastLevel,
      Process__c: formData.process || null,
      Varietal__c: formData.varietal || null,
      Region__c: formData.region || null,
      Country__c: formData.country || null,
      // Cross-border ship-to destinations — bag-size based, a
      // ";"-separated string entered on the whole-bean variant of this bag size
      // (e.g. "NL;DE"). Null when not cross-border.
      Ship_To__c: variant.ship_to || null,
      Brew_Method__c: formData.brewMethod.join(";"),
      Badges__c: formData.badges?.join(";") || null,
      Character__c: formData.character || null,
      Certifications_Accolades_or_Awards__c: formData.certifications?.join(";") || null,
      Elevation_from__c: formData.elevationStart,
      Elevation_to__c: formData.elevationEnd,
      // Provisional Beanz intensity score. Product-level, like the fields
      // above — every bag size of this coffee carries the same score.
      ...buildIntensityPayload(formData, isIntensityEstablished),
      Roaster_notes__c: formData.roasterNotes,
      Roaster_Comments__c: formData.roasterNotes,

      // Variant-level fields
      Bag_size__c: variant.bagSize ?? "",
      Grind__c: variant.grind?.join(";") ?? "",
      MSRP__c: variant.price ?? 0,
      Wholesale_price__c: resolveWholesalePriceForSalesforce(variant, isDeDomestic, ratesByRegion),
      Term__c: variant.term ?? "Long",
      Roaster_effective_date__c: variant.startDate ?? "",
      Weekly_Inventory__c: variant.weeklyInventory ?? 0,
      Total_Inventory_for_Season__c: variant.totalMaxShortTermInventory ?? 0,

      // Context fields
      Is_New_Bag_Sku__c: isNewBagSizeMode,
      Requestor__c: userId,
      Business_unit__c: vendorRegion,
      CurrencyIsoCode: currencyIso,
      Image_Doc__c: imageDoc,
      ...copyEditFields,
    };
  };
}

/** Maps ProductFormData to an array of SalesforceProduct payloads —
 *  one per selected variant (bag size). The API expects an array. `homeRegion`
 *  and `ratesByRegion` drive the DE-domestic KS-excluded wholesale rule
 *  omit them for vendors where it doesn't apply. */
export async function mapFormToSalesforcePayloads(
  formData: ProductFormData,
  userId: string,
  vendorRegion: string,
  currencyIso: string,
  isNewBagSizeMode: boolean,
  localizedFields?: LocalizedFields,
  regionLocales: LocaleObject[] = [],
  homeRegion?: CrossBorderCountries,
  ratesByRegion: TaxRatesByRegion = DEFAULT_TAX_RATES_BY_REGION,
  // New bag-size mode always starts from an existing coffee; a resubmitted
  // rejected request can also carry an established score without being one.
  isIntensityEstablished: boolean = isNewBagSizeMode
): Promise<SalesforceProduct[]> {
  const selectedVariants = formData.variants.filter((variant) => variant.isProductSelectedForImport);
  // Fallback to first variant if none selected
  const variants = selectedVariants.length > 0 ? selectedVariants : [formData.variants[0]];

  const imageDoc = await processImageUpload(formData.imageUploads as UploadImage[] | null | undefined);
  const copyEditFields = buildCopyEditFields(formData, isNewBagSizeMode, localizedFields, regionLocales);

  const setVariantPayload = buildVariantPayload(
    formData,
    userId,
    vendorRegion,
    currencyIso,
    isNewBagSizeMode,
    imageDoc,
    copyEditFields,
    homeRegion,
    ratesByRegion,
    isIntensityEstablished
  );

  return variants.filter((variant): variant is NonNullable<typeof variant> => variant != null).map(setVariantPayload);
}
